import { useEffect, useState } from "react";
import { admin } from "../../api/client";
import { SETTING_KEYS, type Company, type SettingKey } from "../../api/types";
import { can, staffRole, useMe } from "../../auth/session";
import { TocaPage } from "../../components/Shell";
import { Empty, ErrorBox, Field, Modal, Pill, Spinner, errorMessage, useToast } from "../../components/ui";
import { useAsync } from "../../hooks/useAsync";
import { fmt, t } from "../../i18n";
import { dateTimeShort, formatCNPJ } from "../../lib/format";
import { brl, maskDecimalInput, maskedToCents, formatCents } from "../../lib/money";
import { KybStatusPill } from "./shared";

export function TocaConfiguracoes() {
  const me = useMe();
  const editable = can.editSettings(staffRole(me));
  const [key, setKey] = useState<SettingKey>("fee_schedule");
  return (
    <TocaPage title={t.toca.settingsTitle}>
      <p className="ink2" style={{ fontSize: 13.5 }}>{t.toca.settingsLead}</p>
      <div className="chips" role="tablist">
        {SETTING_KEYS.map((k) => (
          <button key={k} type="button" className="chip mono" role="tab" aria-selected={key === k} onClick={() => setKey(k)}>{k}</button>
        ))}
      </div>
      <SettingEditor key={key} settingKey={key} editable={editable} />
      <CompaniesCard editable={can.editCompanies(staffRole(me))} />
      <DeadJobs />
    </TocaPage>
  );
}

function SettingEditor({ settingKey, editable }: { settingKey: SettingKey; editable: boolean }) {
  const toast = useToast();
  const { data, error, loading, reload } = useAsync(() => admin.setting(settingKey), [settingKey]);
  const [text, setText] = useState("");
  const [note, setNote] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (data) setText(JSON.stringify(data.value, null, 2));
  }, [data]);

  async function save() {
    setErr(null);
    let value: unknown;
    try {
      value = JSON.parse(text);
    } catch (e) {
      return setErr(fmt(t.toca.settingInvalidJson, { msg: (e as Error).message }));
    }
    if (!note.trim()) return setErr(t.toca.noteRequired);
    setBusy(true);
    try {
      const r = await admin.putSetting(settingKey, value, note.trim());
      toast(fmt(t.toca.settingSaved, { v: r.version }));
      setNote("");
      void reload();
    } catch (e) {
      setErr(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="two n360">
      <div className="card pad stack s12">
        <div className="row between">
          <h2 className="mono">{settingKey}</h2>
          {data && <Pill tone="mute">{fmt(t.common.version, { v: data.version })}</Pill>}
        </div>
        {loading && !data ? (
          <Spinner />
        ) : error ? (
          <ErrorBox error={error} onRetry={reload} />
        ) : (
          <>
            <Field label={t.toca.settingValue}>
              {(id) => (
                <div className="inp">
                  <textarea id={id} className="mono" value={text} onChange={(e) => setText(e.target.value)} rows={14} style={{ minHeight: 260, fontSize: 12.5 }} readOnly={!editable} spellCheck={false} />
                </div>
              )}
            </Field>
            {editable && (
              <>
                <Field label={t.toca.settingNote}>
                  {(id) => <div className="inp"><input id={id} value={note} onChange={(e) => setNote(e.target.value)} placeholder={t.common.notePlaceholder} /></div>}
                </Field>
                {err && <div className="form-error" role="alert">{err}</div>}
                <div><button type="button" className="btn" onClick={save} disabled={busy}>{t.toca.settingSave}</button></div>
              </>
            )}
            {!editable && <span className="muted small">{t.common.readonlyHint}</span>}
          </>
        )}
      </div>
      <div className="card">
        <div className="card-head"><h2>{t.toca.settingHistory}</h2></div>
        {data && data.history?.length ? (
          <div className="tscroll" tabIndex={0}>
            <table>
              <thead><tr><th>{t.common.version.replace("{v}", "").trim() || "v"}</th><th>{t.common.date}</th><th>{t.common.note}</th></tr></thead>
              <tbody>
                {[...data.history].sort((a, b) => b.version - a.version).map((h) => (
                  <tr key={h.version}>
                    <td className="mono">v{h.version}</td>
                    <td className="muted" style={{ whiteSpace: "nowrap" }}>{dateTimeShort(h.createdAt)}</td>
                    <td className="ink2">{h.note ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <Empty />
        )}
      </div>
    </div>
  );
}

function CompaniesCard({ editable }: { editable: boolean }) {
  const toast = useToast();
  const { data, error, loading, reload } = useAsync(() => admin.companies(), []);
  const [editing, setEditing] = useState<Company | null>(null);
  const [perTx, setPerTx] = useState("");
  const [monthly, setMonthly] = useState("");
  const [takeRate, setTakeRate] = useState("");
  const [blocked, setBlocked] = useState(false);
  const [betaEnd, setBetaEnd] = useState(""); // AAAA-MM-DD; vazio = beta ainda não começou
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  function open(c: Company) {
    setEditing(c);
    setPerTx(c.perTxLimitCents ? formatCents(c.perTxLimitCents) : "");
    setMonthly(c.monthlyLimitCents ? formatCents(c.monthlyLimitCents) : "");
    setTakeRate(c.takeRateOverrideBp !== null && c.takeRateOverrideBp !== undefined ? String(c.takeRateOverrideBp) : "");
    setBlocked(Boolean(c.blockedAt));
    setBetaEnd(c.betaEndsAt ? c.betaEndsAt.slice(0, 10) : "");
    setErr(null);
  }

  async function save() {
    if (!editing) return;
    setBusy(true);
    setErr(null);
    try {
      await admin.updateCompany(editing.id, {
        perTxLimitCents: perTx ? maskedToCents(perTx) : undefined,
        monthlyLimitCents: monthly ? maskedToCents(monthly) : undefined,
        takeRateOverrideBp: takeRate === "" ? null : Number(takeRate),
        betaEndsAt: betaEnd ? `${betaEnd}T23:59:59-03:00` : null,
        blocked,
      });
      toast(t.toca.companySaved);
      setEditing(null);
      void reload();
    } catch (e) {
      setErr(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card">
      <div className="card-head"><h2>{t.toca.companiesTitle}</h2></div>
      {loading && !data ? (
        <Spinner />
      ) : error ? (
        <div style={{ padding: 16 }}><ErrorBox error={error} onRetry={reload} /></div>
      ) : !data || data.length === 0 ? (
        <Empty />
      ) : (
        <div className="tscroll" tabIndex={0}>
          <table>
            <thead>
              <tr>
                <th>{t.settings.company}</th>
                <th>{t.toca.colCnpj}</th>
                <th>{t.common.status}</th>
                <th className="r">{t.toca.colPerTx}</th>
                <th className="r">{t.toca.colMonthly}</th>
                <th className="r">{t.toca.colTakeRate}</th>
                <th>{t.toca.colBetaEnds}</th>
                <th>{t.toca.colBlocked}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {data.map((c) => (
                <tr key={c.id}>
                  <td style={{ fontWeight: 600 }}>{c.legalName}</td>
                  <td className="mono">{formatCNPJ(c.cnpj)}</td>
                  <td><KybStatusPill status={c.status} /></td>
                  <td className="mono r">{c.perTxLimitCents ? brl(c.perTxLimitCents) : "—"}</td>
                  <td className="mono r">{c.monthlyLimitCents ? brl(c.monthlyLimitCents) : "—"}</td>
                  <td className="mono r">{c.takeRateOverrideBp ?? "—"}</td>
                  <td className="mono">{c.betaEndsAt ? c.betaEndsAt.slice(0, 10).split("-").reverse().join("/") : "—"}</td>
                  <td>{c.blockedAt ? <Pill tone="crit">{t.common.yes}</Pill> : <span className="muted">{t.common.no}</span>}</td>
                  <td className="r">{editable && <button type="button" className="btn ghost xs" onClick={() => open(c)}>{t.toca.edit}</button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {editing && (
        <Modal title={`${t.toca.edit} · ${editing.legalName}`} onClose={() => !busy && setEditing(null)}>
          <div className="grid2" style={{ gap: 12 }}>
            <Field label={t.toca.colPerTx}>{(id) => <div className="inp"><span className="pre mono">R$</span><input id={id} className="mono" inputMode="decimal" value={perTx} onChange={(e) => setPerTx(maskDecimalInput(e.target.value))} /></div>}</Field>
            <Field label={t.toca.colMonthly}>{(id) => <div className="inp"><span className="pre mono">R$</span><input id={id} className="mono" inputMode="decimal" value={monthly} onChange={(e) => setMonthly(maskDecimalInput(e.target.value))} /></div>}</Field>
          </div>
          <div className="grid2" style={{ gap: 12 }}>
            <Field label={t.toca.colTakeRate} hint={t.toca.takeRateHint}>
              {(id, d) => <div className="inp"><input id={id} className="mono" inputMode="numeric" value={takeRate} onChange={(e) => setTakeRate(e.target.value.replace(/\D/g, ""))} aria-describedby={d} /></div>}
            </Field>
            <Field label={t.toca.colBetaEnds} hint={t.toca.betaEndsHint}>
              {(id, d) => <div className="inp"><input id={id} type="date" className="mono" value={betaEnd} onChange={(e) => setBetaEnd(e.target.value)} aria-describedby={d} /></div>}
            </Field>
          </div>
          <label className="check"><input type="checkbox" checked={blocked} onChange={(e) => setBlocked(e.target.checked)} /><span>{t.toca.colBlocked}</span></label>
          {err && <div className="form-error" role="alert">{err}</div>}
          <div className="row" style={{ justifyContent: "flex-end" }}>
            <button type="button" className="btn ghost" onClick={() => setEditing(null)} disabled={busy}>{t.common.cancel}</button>
            <button type="button" className="btn" onClick={save} disabled={busy}>{t.common.save}</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function DeadJobs() {
  const me = useMe();
  const toast = useToast();
  const { data, error, reload } = useAsync(() => admin.deadJobs(), []);
  const [busyId, setBusyId] = useState<string | null>(null);
  if (error || !data || data.length === 0) return null;
  const allowed = can.retryJobs(staffRole(me));
  return (
    <div className="card">
      <div className="card-head"><h2>{t.toca.deadJobs}</h2><Pill tone="crit">{data.length}</Pill></div>
      <div className="tscroll" tabIndex={0}>
        <table>
          <thead><tr><th>id</th><th>type</th><th>attempts</th><th>lastError</th><th /></tr></thead>
          <tbody>
            {data.map((j) => (
              <tr key={j.id}>
                <td className="mono small">{j.id}</td>
                <td className="mono">{j.type}</td>
                <td className="mono">{j.attempts}</td>
                <td className="ink2 small" style={{ maxWidth: 380, overflowWrap: "anywhere" }}>{j.lastError ?? "—"}</td>
                <td className="r">{allowed && <button type="button" className="btn ghost xs" disabled={busyId === j.id} onClick={async () => { if (busyId) return; setBusyId(j.id); try { await admin.retryJob(j.id); toast(t.toca.retry); void reload(); } catch (e) { toast(errorMessage(e)); } finally { setBusyId(null); } }}>{t.toca.retry}</button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
